// hooks/useOrganizations.tsx

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createOrganization,
  deleteOrganization,
  getAllOrganizations,
  getAllowedOrganizations,
  getOrganizationById,
  updateOrganization,
} from '@/services/organization/organizationService';
import { CreateOrganizationPayload, UpdateOrganizationPayload } from '@/types/organization';

type UpdateOrganizationVariables = {
  organizationId: string;
  organizationData: UpdateOrganizationPayload;
};

// Organization CRUD Hooks

// Get all organizations (useQuery for GET operations)
export function useGetAllOrganizations(enabled: boolean = true) {
  return useQuery({
    queryKey: ['organizations', 'all'],
    queryFn: getAllOrganizations,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for navigation
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Use cached data if available
    placeholderData: (previousData) => previousData, // Show cached data while refetching
  });
}

// Get allowed organizations for the Pick Organization modal (useQuery for GET operations)
export function useGetAllowedOrganizations(enabled: boolean = true) {
  return useQuery({
    queryKey: ['organizations', 'allowed'],
    queryFn: getAllowedOrganizations,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for navigation
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Use cached data if available
    placeholderData: (previousData) => previousData, // Show cached data while refetching
  });
}

// Get organization by ID (useQuery for GET operations)
export function useGetOrganizationById(organizationId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['organizations', organizationId],
    queryFn: () => getOrganizationById(organizationId),
    enabled: enabled && !!organizationId,
  });
}

// Create organization (useMutation for POST operations)
export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['create-organization'],
    mutationFn: async (organizationData: CreateOrganizationPayload) =>
      createOrganization(organizationData),
    onSuccess: () => {
      // Automatically invalidate all organization queries
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

// Update organization (useMutation for PUT operations)
export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['update-organization'],
    mutationFn: async ({ organizationId, organizationData }: UpdateOrganizationVariables) =>
      updateOrganization(organizationId, organizationData),
    onSuccess: () => {
      // Automatically invalidate all organization queries
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

// Delete organization (useMutation for DELETE operations)
export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['delete-organization'],
    mutationFn: async (organizationId: string) => deleteOrganization(organizationId),
    onSuccess: () => {
      // Automatically invalidate all organization queries
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

